/*!

=========================================================
* Vision UI Free React - v1.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/vision-ui-free-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com/)
* Licensed under MIT (https://github.com/creativetimofficial/vision-ui-free-react/blob/master LICENSE.md)

* Design and Coded by Simmmple & Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/

import { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";

// @mui material components
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import TablePagination from "@mui/material/TablePagination";

// Vision UI Dashboard React components
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";

// Vision UI Dashboard React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";

// Mytherra API & Wallet Services
import mytherraApi from "services/mytherraApi";
import walletService from "services/walletService";
import storageService from "services/storageService";

// Data
import transactionsTableData from "layouts/tables/data/transactionsTableData";

function Tables() {
  const history = useHistory();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const rowsPerPage = 20; // Fest auf 20 pro Seite

  // RPC Configuration
  const API_BASE = window.location.hostname === 'localhost' ? 'http://localhost:3002' : '/api';
  const RPC_URL = `${API_BASE}/rpc`;
  const RPC_USER = '';
  const RPC_PASSWORD = '';

  useEffect(() => {
    const fetchTransactions = async () => {
      try {
        setLoading(true);

        // Check if user has a session
        const privateKey = storageService.loadSession();
        
        if (!privateKey) {
          console.log('No wallet session found, redirecting to sign in');
          history.push('/sign-in');
          return;
        }

        // Get address from private key
        const address = await walletService.getAddressFromWif(privateKey);
        
        if (!address) {
          console.error('Invalid private key in session');
          storageService.clearSession();
          history.push('/sign-in');
          return;
        }

        // Try Explorer API first
        try {
          const addressData = await mytherraApi.getExplorerAddress(address);
          console.log('Tables - Full address data from explorer:', addressData);
          
          if (addressData && addressData.txs && Array.isArray(addressData.txs) && addressData.txs.length > 0) {
            // txs is an array of transaction IDs, fetch details via Explorer API
            const txDetails = await Promise.all(
              addressData.txs.map(async (txid) => {
                try {
                  // Use Explorer API via proxy to get transaction details
                  const response = await fetch(`${API_BASE}/explorer/api/getrawtransaction?txid=${txid}&decrypt=1`);
                  if (!response.ok) {
                    console.log(`Failed to fetch tx ${txid}: ${response.status}`);
                    return null;
                  }
                  
                  const txData = await response.json();
                  if (!txData || txData.error) {
                    console.log(`Error in tx data for ${txid}:`, txData?.error);
                    return null;
                  }
                  
                  // Determine if this is a send or receive
                  const isInputTx = addressData.input_txs && addressData.input_txs.includes(txid);
                  
                  let amount = 0;
                  let category = 'receive';
                  
                  if (isInputTx) {
                    category = 'send';
                    for (const vout of txData.vout || []) {
                      if (vout.scriptPubKey && vout.scriptPubKey.address !== address) {
                        amount += vout.value;
                      }
                    }
                    amount = -amount;
                  } else {
                    for (const vout of txData.vout || []) {
                      if (vout.scriptPubKey && vout.scriptPubKey.address === address) {
                        amount += vout.value;
                      }
                    }
                  }
                  
                  // Get timestamp - try multiple sources
                  let timestamp = txData.time || txData.blocktime;
                  
                  // If no timestamp and we have a blockhash, fetch the block to get the time
                  if (!timestamp && txData.blockhash) {
                    try {
                      const blockResponse = await fetch(`${API_BASE}/explorer/api/getblock?hash=${txData.blockhash}`);
                      if (blockResponse.ok) {
                        const blockData = await blockResponse.json();
                        timestamp = blockData.time || blockData.blocktime;
                      }
                    } catch (blockErr) {
                      console.log('Could not fetch block time for', txData.blockhash);
                    }
                  }
                  
                  // Last resort: use current time (but log a warning)
                  if (!timestamp) {
                    console.warn('No timestamp found for tx', txid, '- using current time');
                    timestamp = Date.now() / 1000;
                  }
                  
                  return {
                    txid: txid,
                    amount: amount,
                    category: category,
                    confirmations: txData.confirmations || 0,
                    time: timestamp
                  };
                } catch (err) {
                  console.log('Could not fetch tx details for', txid, err);
                  return null;
                }
              })
            );
            
            // Filter out null values and sort by time
            const txs = txDetails.filter(tx => tx !== null);
            txs.sort((a, b) => b.time - a.time);
            
            console.log('Tables - Parsed transactions:', txs);
            
            if (txs.length > 0) {
              setTransactions(txs);
              setLoading(false);
              return;
            }
          }
        } catch (explorerError) {
          console.log('Explorer API error:', explorerError);
        }
        
        // Fallback: Use RPC listTransactions to get ALL transactions (send + receive)
        const allTransactions = await mytherraApi.listTransactions(1000); // Get last 1000 transactions
        
        if (allTransactions && allTransactions.length > 0) {
          // Filter transactions for this address and sort by time
          const addressTxs = allTransactions
            .filter(tx => tx.address === address || !tx.address) // Include tx for this address
            .map(tx => ({
              txid: tx.txid,
              vout: tx.vout || 0,
              amount: tx.amount,
              confirmations: tx.confirmations || 0,
              category: tx.category || 'receive',
              time: tx.time || tx.blocktime || Date.now() / 1000,
              address: tx.address
            }));
          
          // Sort by time (newest first)
          addressTxs.sort((a, b) => b.time - a.time);
          setTransactions(addressTxs);
        } else {
          setTransactions([]);
        }

        setLoading(false);
      } catch (error) {
        console.error("Error fetching transactions:", error);
        setLoading(false);
      }
    };

    // Initial fetch
    fetchTransactions();

    // Refresh every 30 seconds
    const interval = setInterval(fetchTransactions, 30000);

    return () => clearInterval(interval);
  }, [history]);

  // Calculate total pages
  const totalPages = Math.ceil(transactions.length / rowsPerPage);

  // Get paginated transactions (page starts at 1)
  const paginatedTransactions = transactions.slice(
    (page - 1) * rowsPerPage,
    (page - 1) * rowsPerPage + rowsPerPage
  );

  const { columns, rows } = transactionsTableData(paginatedTransactions);

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 7;
    
    if (totalPages <= maxPagesToShow) {
      // Show all pages
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show first, last, and pages around current
      if (page <= 4) {
        for (let i = 1; i <= 5; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (page >= totalPages - 3) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 4; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = page - 1; i <= page + 1; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <VuiBox py={3}>
        <Card>
          <VuiBox display="flex" justifyContent="space-between" alignItems="center" mb="32px">
            <VuiBox>
              <VuiTypography variant="lg" color="white" fontWeight="bold">
                All Transactions
              </VuiTypography>
              <VuiTypography variant="button" color="text" fontWeight="regular">
                Complete transaction history from your wallet
              </VuiTypography>
            </VuiBox>
            <VuiBox display="flex" alignItems="center">
              <Icon
                sx={{
                  color: loading ? "warning.main" : "success.main",
                  fontSize: "20px !important",
                  mr: 1,
                }}
              >
                {loading ? "sync" : "check_circle"}
              </Icon>
              <VuiTypography variant="button" color="text">
                {loading ? "Loading..." : `${transactions.length} transactions`}
              </VuiTypography>
            </VuiBox>
          </VuiBox>
          <VuiBox
            sx={{
              "& th": {
                borderBottom: ({ borders: { borderWidth }, palette: { grey } }) =>
                  `${borderWidth[1]} solid ${grey[700]}`,
              },
              "& .MuiTableRow-root:not(:last-child)": {
                "& td": {
                  borderBottom: ({ borders: { borderWidth }, palette: { grey } }) =>
                    `${borderWidth[1]} solid ${grey[700]}`,
                },
              },
            }}
          >
            <Table columns={columns} rows={rows} />
          </VuiBox>
          
          {/* Simple Pagination - inside Card */}
          {transactions.length > 0 && (
            <VuiBox display="flex" justifyContent="center" alignItems="center" p={3} gap={1}>
              <VuiTypography color="text" variant="caption" mr={2}>
                Showing {((page - 1) * rowsPerPage) + 1}-{Math.min(page * rowsPerPage, transactions.length)} of {transactions.length}
              </VuiTypography>
              {totalPages > 1 && (
                <>
                  {getPageNumbers().map((pageNum, index) => {
                    if (pageNum === '...') {
                      return (
                        <VuiTypography key={`ellipsis-${index}`} color="text" variant="button" px={1}>
                          ...
                        </VuiTypography>
                      );
                    }
                    
                    return (
                      <VuiBox
                        key={pageNum}
                        onClick={() => setPage(pageNum)}
                        sx={{
                          minWidth: "40px",
                          height: "40px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          borderRadius: "8px",
                          cursor: "pointer",
                          background: page === pageNum 
                            ? "linear-gradient(135deg, #0075F6 0%, #00CED1 100%)"
                            : "rgba(255, 255, 255, 0.05)",
                          transition: "all 0.2s",
                          "&:hover": {
                            background: page === pageNum
                              ? "linear-gradient(135deg, #0075F6 0%, #00CED1 100%)"
                              : "rgba(255, 255, 255, 0.1)",
                          },
                        }}
                      >
                        <VuiTypography
                          variant="button"
                          color="white"
                          fontWeight={page === pageNum ? "bold" : "regular"}
                        >
                          {pageNum}
                        </VuiTypography>
                      </VuiBox>
                    );
                  })}
                </>
              )}
            </VuiBox>
          )}
        </Card>
      </VuiBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Tables;
